import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { X, Search, Briefcase } from 'lucide-react';

// Helper to get borrower name from various possible field names
// The 'name' field often contains the borrower name in this system
const getBorrowerName = (c) => {
    return c.borrowerName || c.borrower_name || c.borrower || c.name || null;
};

// Helper to get case identifier (number or ID)
const getCaseIdentifier = (c) => {
    if (c.caseNumber) return `Case #${c.caseNumber}`;
    if (c.case_number) return `Case #${c.case_number}`;
    return `Case #${c.id}`;
};

const CasePicker = ({ cases = [], onSelect, onClose }) => {
    const [searchTerm, setSearchTerm] = useState('');

    const filteredCases = useMemo(() => {
        if (!searchTerm) return cases;
        const term = searchTerm.toLowerCase();
        return cases.filter(c => {
            const borrower = getBorrowerName(c);
            const caseId = getCaseIdentifier(c);
            return (
                borrower?.toLowerCase().includes(term) ||
                caseId?.toLowerCase().includes(term) ||
                String(c.id).includes(term)
            );
        });
    }, [cases, searchTerm]);

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-start justify-center pt-20 p-4 dark:bg-slate-950/90 bg-slate-500/50 backdrop-blur-sm"
            onClick={onClose}
        >
            <motion.div
                initial={{ scale: 0.95, opacity: 0, y: -20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.95, opacity: 0, y: -20 }}
                transition={{ duration: 0.2, ease: 'easeInOut' }}
                className="w-full max-w-lg flex flex-col dark:bg-slate-900 bg-white rounded-2xl shadow-2xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Search Header */}
                <div className="p-4 border-b dark:border-slate-800 border-slate-200">
                    <div className="relative">
                        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                        <input
                            type="text"
                            placeholder="Search by case name, borrower, or ID..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            autoFocus
                            className="w-full pl-10 pr-4 py-2.5 dark:bg-slate-800 bg-slate-100 border dark:border-slate-700 border-slate-200 rounded-lg dark:text-white text-slate-900 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 transition"
                        />
                    </div>
                </div>

                {/* Case List */}
                <div className="flex-1 overflow-y-auto max-h-96">
                    {filteredCases.length > 0 ? (
                        <ul>
                            {filteredCases.map(c => {
                                const borrower = getBorrowerName(c);
                                const caseId = getCaseIdentifier(c);
                                return (
                                    <li key={c.id}>
                                        <button
                                            onClick={() => onSelect({ id: c.id, name: borrower || caseId })}
                                            className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition"
                                        >
                                            <Briefcase size={16} className="text-slate-400 flex-shrink-0" />
                                            <div className="min-w-0">
                                                <p className="font-medium dark:text-slate-200 text-slate-800 truncate">
                                                    {borrower || caseId}
                                                </p>
                                                <p className="text-sm dark:text-slate-500 text-slate-400 truncate">
                                                    {caseId}
                                                </p>
                                            </div>
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    ) : (
                        <div className="text-center py-12">
                            <p className="dark:text-slate-400 text-slate-500">No cases found.</p>
                        </div>
                    )}
                </div>
            </motion.div>
        </motion.div>
    );
};

export default CasePicker;
